// Pure signal-catalog helpers for the custom-alarm builder's signal picker
// (custom+conditional alarms, phase 3). This is the load-bearing module of the
// whole picker: it turns a boat's ACCEPTED telemetry paths — the exact set the
// cloud compiler binds against (`boat_paths WHERE accepted`, the catalog
// `cloud/src/worker/rule-compiler.ts` feeds `compileDefinitions`) — into
// grouped, human-labelled, searchable entries. The honesty invariant is upstream
// (the endpoint only serves bindable paths); this module never invents a signal,
// only presents one.
//
// Everything here is pure and unit-tested. The freshness bands deliberately
// reuse `deviceSeen.freshnessClass` so the picker's freshness dot and the Devices
// screen's "last seen" dot bucket on the same thresholds (one source of truth).

import { signalLabel } from '@neomaris/protocol';

import { freshnessClass } from './deviceSeen';

/** Value kind as served by `GET /v1/boats/:boatId/signals` — the JSON type of
 *  the latest sample. Numeric signals get High/Low triggers, booleans get
 *  On/Off; `string`/`object` support only DataUnavailable in v1. `unknown` is an
 *  accepted, bindable path with no sample in retention yet — distinct from
 *  `object` so the picker can tell "never seen a value" from "value is
 *  structured" (see {@link suggestedTriggerClasses}). */
export type SignalKind = 'number' | 'boolean' | 'string' | 'object' | 'unknown';

/** The last observed value of a signal, scalar-only. The endpoint flattens
 *  structured payloads (e.g. `navigation.position`) to `null` — a picker renders
 *  a live scalar hint or nothing, never a JSON blob. `null` is also the family's
 *  legitimate "unavailable" state and the value of any never-sampled path. */
export type SignalValue = number | boolean | string | null;

/** One catalog entry, mirroring the `GET /v1/boats/:boatId/signals` row shape:
 *  an accepted path merged with its latest sample and owning device. Timestamps
 *  are ISO strings; `lastSeen`/`firstSeen` are the always-present `boat_paths`
 *  stamps, while value/unit/device fields (and `lastValueAt`) are null when the
 *  boat has an accepted path but no captured sample or resolved device yet. */
export interface Signal {
  path: string;
  unit: string | null;
  kind: SignalKind;
  lastValue: SignalValue;
  lastValueAt: string | null;
  lastSeen: string;
  firstSeen: string;
  deviceId: string | null;
  deviceName: string | null;
  /** Operator-facing name for this specific channel (e.g. "Mid Bilge High
   *  Water"), when the producing device labels its channels. Null otherwise. */
  channelLabel?: string | null;
  displayLabel?: string;
  entityName?: string;
}

/** The picker's top-level grouping bucket. */
export type SignalGroup =
  | 'Engine'
  | 'Batteries'
  | 'Switches'
  | 'Electrical'
  | 'Tanks'
  | 'Environment'
  | 'Navigation'
  | 'Steering'
  | 'Derived'
  | 'Other';

/** The trigger classes v1 offers, by signal kind. */
export type TriggerClass = 'High' | 'Low' | 'On' | 'Off' | 'DataUnavailable';

/** Freshness band for a signal's latest value, matching the Devices screen. */
export type SignalFreshness = 'fresh' | 'stale' | 'cold';

/**
 * Group a path into a picker bucket. Order matters: the two `electrical.*`
 * special cases (batteries, switches) are checked before the generic
 * `electrical` fallback so a battery voltage never lands in "Electrical".
 */
export function signalGroup(path: string): SignalGroup {
  const seg = path.split('.');
  switch (seg[0]) {
    case 'propulsion':
      return 'Engine';
    case 'electrical':
      if (seg[1] === 'batteries') return 'Batteries';
      if (seg[1] === 'switches') return 'Switches';
      return 'Electrical';
    case 'tanks':
      return 'Tanks';
    case 'environment':
      return 'Environment';
    case 'navigation':
      return 'Navigation';
    case 'steering':
      return 'Steering';
    case 'computed':
      return 'Derived';
    default:
      return 'Other';
  }
}

// Human names for signal paths live in @neomaris/protocol (signal-label.ts), so
// the cloud names a reading the way this picker does: Neo's charts (ADR 0230)
// carry each reading's on-screen label, and its telemetry tools the on-screen
// name of each engine, battery and tank reading. Re-exported unchanged.
export { signalLabel };

/**
 * Trigger classes to offer for a signal kind. Numbers get High/Low, booleans
 * get On/Off; every kind can go DataUnavailable ("stops reporting"). Text,
 * structured (`object`) and any unrecognized future kind support only
 * DataUnavailable in v1 — hence a `string` parameter with a catch-all default.
 */
export function suggestedTriggerClasses(kind: string, path?: string): TriggerClass[] {
  // Switch/indicator bank channels are flags that happen to travel as 1/0
  // NUMBERS (the Signal K mapper emits numbers, and the telemetry allowlist has
  // to accept them as numeric or ingest drops them). Offering High/Low here
  // would ask the operator to write "bilge alarm above 0.5", which is both
  // absurd and easy to get backwards. They are on/off, so offer on/off.
  if (path !== undefined && isSwitchChannelPath(path)) return ['On', 'Off', 'DataUnavailable'];
  if (kind === 'number') return ['High', 'Low', 'DataUnavailable'];
  if (kind === 'boolean') return ['On', 'Off', 'DataUnavailable'];
  return ['DataUnavailable'];
}

/**
 * The display name for a whole catalog entry — {@link signalLabel} with every
 * field it can use already filled in.
 *
 * Five surfaces each had their own private `labelOf(signal)` doing this, and
 * when `channelLabel` was added they all silently kept composing the positional
 * name: an operator could search "mid bilge", pick "Mid Bilge High Water", and
 * get an alarm auto-named "Switch bank 2 channel 4 turns on". One helper so a
 * new field cannot go missing from four of five call sites again.
 */
export function signalDisplayName(signal: Signal): string {
  return (
    signal.displayLabel ??
    signalLabel(signal.path, {
      deviceName: signal.deviceName,
      unit: signal.unit,
      channelLabel: signal.channelLabel,
      entityName: signal.entityName,
    })
  );
}

/** True for `electrical.switches.bank.<bank>.<channel>.state` — a binary
 *  switch/indicator channel carried as a 1/0 number. */
export function isSwitchChannelPath(path: string): boolean {
  const seg = path.split('.');
  return (
    seg.length === 6 &&
    seg[0] === 'electrical' &&
    seg[1] === 'switches' &&
    seg[2] === 'bank' &&
    seg[5] === 'state'
  );
}

/**
 * Freshness band for a signal's latest value, given an ISO timestamp
 * (`lastValueAt` or `lastSeen`) and the current epoch-ms. Delegates the band
 * boundaries to {@link freshnessClass} so the picker agrees with the Devices
 * screen. A missing / empty timestamp is `cold`: an accepted path with no
 * captured value is honestly "no recent data", never a false "fresh".
 */
export function freshness(
  iso: string | null | undefined,
  now: number = Date.now()
): SignalFreshness {
  if (iso === null || iso === undefined || iso === '') return 'cold';
  return freshnessClass(iso, now);
}

// ---------------------------------------------------------------------------
// Instance families — "the same measurement on the other engine".
//
// A path's INSTANCE SEGMENT is the stable N2K data instance (ADR-097 §4): the
// `0` in `propulsion.0.oilPressure`, the `house` in
// `electrical.batteries.house.voltage`. Two paths are SIBLINGS when everything
// but that one segment matches — port and starboard oil pressure, house and
// start battery voltage — which is what makes "Duplicate for the other engine"
// a safe offer: same measurement, same unit, same thresholds.
//
// The families are EXACTLY the ones {@link signalLabel} composes by hand, and
// deliberately no more. Outside them we do not know which segment is the
// instance, and guessing would pair `tanks.fuel.0.currentLevel` with
// `tanks.freshWater.0.currentLevel` — a different tank, not another instance of
// the same one. An unknown shape simply has no siblings.
// ---------------------------------------------------------------------------

/** A path split around its instance segment. */
export interface PathInstance {
  /** Everything before the instance, joined — `propulsion`, `electrical.batteries`. */
  readonly head: string;
  /** The instance segment itself — `0`, `house`. */
  readonly instance: string;
  /** Everything after it, joined — `oilPressure`, `voltage`. */
  readonly leaf: string;
}

/**
 * Split a path around its instance segment, or `null` when this build cannot
 * say which segment that is. Recognised shapes:
 *
 *   - `propulsion.<instance>.<measurement…>`
 *   - `electrical.<group>.<instance>.<measurement…>` (batteries, switches, …)
 *   - `tanks.<type>.<instance>.<measurement…>`
 */
export function pathInstance(path: string): PathInstance | null {
  const seg = path.split('.').filter((s) => s.length > 0);
  const root = seg[0];
  const index = root === 'propulsion' ? 1 : root === 'electrical' || root === 'tanks' ? 2 : -1;
  if (index === -1) return null;
  // The instance needs a measurement after it, or the "same leaf" test compares
  // two empty strings and every instance looks like a sibling of every other.
  if (seg.length < index + 2) return null;
  const instance = seg[index];
  if (instance === undefined) return null;
  return {
    head: seg.slice(0, index).join('.'),
    instance,
    leaf: seg.slice(index + 1).join('.'),
  };
}

/**
 * The catalog signals that are the SAME measurement on another instance — the
 * other engine's oil pressure, the other bank's voltage. Sorted by path, and
 * never including `path` itself.
 *
 * Empty whenever the family is unrecognised (see {@link pathInstance}): offering
 * a "duplicate" that silently watched a different thing is the one outcome worth
 * avoiding here.
 */
export function siblingSignals(path: string, catalog: readonly Signal[]): Signal[] {
  const self = pathInstance(path);
  if (self === null) return [];
  return catalog
    .filter((s) => {
      if (s.path === path) return false;
      const other = pathInstance(s.path);
      return (
        other !== null &&
        other.head === self.head &&
        other.leaf === self.leaf &&
        other.instance !== self.instance
      );
    })
    .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}

/**
 * Lowercase search haystack for a signal: its human label, raw path, group and
 * device name, joined. Lets the picker match on the friendly name ("oil
 * pressure"), the technical path ("propulsion.0.oilPressure"), the group
 * ("engine") or the device it came from.
 *
 * A labelled channel contributes BOTH names: the installer's ("Mid Bilge High
 * Water") and the composed positional one ("Switch bank 2 channel 4"). Someone
 * who knows the boat searches "mid bilge"; someone reading a wiring diagram
 * searches the bank and channel. Both have to land on the same row.
 */
export function searchHaystack(signal: Signal): string {
  const positional = signalLabel(signal.path, {
    deviceName: signal.deviceName,
    unit: signal.unit,
  });
  const group = signalGroup(signal.path);
  return [
    signal.displayLabel ?? '',
    signal.entityName ?? '',
    signal.channelLabel ?? '',
    positional,
    signal.path,
    group,
    signal.deviceName ?? '',
  ]
    .join(' ')
    .toLowerCase();
}
